import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Spin, Typography, Card, Space, Alert } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/logo.svg';
import styled from 'styled-components';
import { colors } from '../theme/tokens/colors';
import { spacing } from '../theme/tokens/spacing';

const { Title } = Typography;

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: ${spacing.xl};
  background-color: ${colors.background.light};
`;

const LoginCard = styled(Card)`
  width: 100%;
  max-width: 400px;
  padding: ${spacing.lg};
`;

const Logo = styled.img`
  width: 180px;
  margin-bottom: ${spacing.lg};
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${colors.background.overlay};
`;

const StyledAlert = styled(Alert)`
  margin-bottom: ${spacing.md};
`;

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // Function to clear all cookies
  const clearAllCookies = () => {
    const cookies = document.cookie.split(';');
    
    for (let cookie of cookies) {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
    }
    console.log('🗑️ Alle Cookies wurden gelöscht');
  };

  useEffect(() => {
    const init = async () => {
      // First clear all cookies and logout
      clearAllCookies();
      try {
        await logout();
      } catch (error) {
        console.log('Logout error (expected if not logged in):', error);
      }
    };
    
    init();
  }, []); // Only run on mount

  useEffect(() => {
    // Separate effect for handling authentication state
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (values) => {
    setLoading(true);
    setError('');
    
    try {
      const success = await login(values.username, values.password);
      if (success) {
        navigate('/dashboard');
      } else {
        setError('Login fehlgeschlagen. Bitte überprüfen Sie Ihre Anmeldedaten.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Login fehlgeschlagen. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer style={{ backgroundColor: colors.background.dark }}>
      <Space direction="vertical" align="center" size={spacing.lg}>
        <div style={{ textAlign: 'center' }}>
          <Logo src={logo} alt="Hyplife Logo" />
        </div>
        
        <LoginCard>
          <Space direction="vertical" size={spacing.lg} style={{ width: '100%' }}>
            {error && (
              <StyledAlert
                message="Anmeldefehler"
                description={error}
                type="error"
                showIcon
              />
            )}
            
            <Form
              form={form}
              onFinish={handleSubmit}
              layout="vertical"
              requiredMark={false}
              size="large"
            >
              <Form.Item
                name="username"
                label="Benutzername"
                rules={[{ required: true, message: 'Bitte geben Sie Ihren Benutzernamen ein' }]}
              >
                <Input disabled={loading} />
              </Form.Item>

              <Form.Item
                name="password"
                label="Passwort"
                rules={[{ required: true, message: 'Bitte geben Sie Ihr Passwort ein' }]}
              >
                <Input.Password disabled={loading} />
              </Form.Item>

              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={loading}
                  block
                >
                  Anmelden
                </Button>
              </Form.Item>
            </Form>
          </Space>
        </LoginCard>
      </Space>
      
      {loading && (
        <LoadingOverlay>
          <Spin size="large" />
        </LoadingOverlay>
      )}
    </LoginContainer>
  );
};

export default Login;
